// callService.js
import { createEmitter } from './emitter';
import { ensurePeer, makeOffer, cleanupPeer } from './service/peer';
import { wireSignaling } from './service/signaling';
import {
    getMedia,
    reactivateTracks,
    listCameras,
    switchCameraTrack,
    attachAudioGuards,
} from './service/media';

import { applyLeaderFlags } from './service/roles';
import { beginReconnection, tryIceRestartIfLeader, maybeIceRestart } from './service/reconnect';

export default class CallService {
    constructor({ socket, media, iceServers }) {
        this.socket = socket;
        this.media = media;
        this.iceServers = iceServers || [];
        this.emitter = createEmitter();

        this.pc = null;
        this.localStream = null;
        this.remoteStream = new this.media.MediaStream();

        this.state = 'idle';
        this.partner = null;
        this.callId = null;
        this.chatId = null;
        this.mode = 'audio';
        this.video = false;
        this.myId = null;

        this._facing = 'user';
        this.role = null;
        this.isLeader = false;
        this.isPolite = false;
        this.makingOffer = false;
        this.ignoreOffer = false;

        this._pendingCandidates = [];
        this._ringTimer = null;
        this._statsTimer = null;
        this._reconnectTimer = null;
        this._reconnecting = false;
        this._reconnectStartedAt = 0;
        this._connectedAt = null;
        this._lastIncomingCallId = null;
        this._lastAcceptedCallId = null;

        this._kaChannel = null;
        this._kaTimer = null;
        this._wakeLock = null;
        this._mediaDeviceChangeHandler = null;

        wireSignaling(this);

        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', () => {
                try {
                    if (!this.callId || this.state === 'idle') return;

                    // Закрытие одной из вкладок, где только звонит входящий вызов,
                    // НЕ должно сбрасывать звонок на остальных устройствах аккаунта.
                    if (this.role === 'callee' && this.state === 'ringing') return;

                    const eventName = (
                        this.role === 'caller'
                        && (this.state === 'dialing' || this.state === 'ringing')
                    ) ? 'call:cancel' : 'call:end';

                    this.socket.emit(eventName, {
                        callId: this.callId,
                        reason: 'ended',
                    });
                } catch (_error) { }
            });
        }
    }

    on(evt, cb) {
        this.emitter.on(evt, cb);
        return () => this.emitter.off(evt, cb);
    }

    getLocalStream() {
        return this.localStream;
    }

    getRemoteStream() {
        return this.remoteStream;
    }

    resumeSignaling() {
        if (!this.callId || this.state === 'idle' || !this.socket.connected) return false;

        this.socket.emit('call:resume', {
            callId: this.callId,
        });

        return true;
    }

    async start({ callId, from, to, video, chatId = null }) {
        if (this.state !== 'idle') throw new Error('busy');

        this.myId = from || window.__ME_ID__ || null;
        if (this.myId && typeof window !== 'undefined') {
            window.__ME_ID__ = String(this.myId);
        }

        this.callId = callId;
        this.partner = to;
        this.chatId = chatId;
        this.video = !!video;
        this.mode = video ? 'video' : 'audio';
        this.role = 'caller';
        this._deriveLeaderFlags();
        this.state = 'dialing';

        try {
            await this._getMedia(!!video);

            if (this.callId !== callId || this.state !== 'dialing') {
                throw new Error('call-state-changed');
            }

            this.state = 'ringing';

            this.socket.emit('call:invite', {
                callId,
                to,
                video: !!video,
                mode: this.mode,
                chatId,
            });

            this.emitter.emit('ring:start', { mode: 'outgoing' });

            clearTimeout(this._ringTimer);
            this._ringTimer = setTimeout(() => {
                if (this.state !== 'ringing' || this.callId !== callId) return;

                try {
                    this.socket.emit('call:cancel', {
                        callId,
                        reason: 'missed',
                    });
                } catch (_error) { }

                this._cleanup('missed');
            }, 46_000);
        } catch (error) {
            if (this.callId === callId) this._cleanup('start-failed');
            throw error;
        }
    }

    async accept({ callId, from, to, video }) {
        if (this.state !== 'idle' && this.state !== 'ringing') return;

        this.myId = to || window.__ME_ID__ || null;
        if (this.myId && typeof window !== 'undefined') {
            window.__ME_ID__ = String(this.myId);
        }

        this.callId = callId;
        this.partner = from;
        this.video = !!video;
        this.mode = video ? 'video' : 'audio';
        this.role = 'callee';
        this._deriveLeaderFlags();

        await this._getMedia(!!video);

        // Пока браузер спрашивал разрешение на микрофон, звонок мог быть принят
        // на другом устройстве. В этом случае не отправляем поздний accept.
        if (this.callId !== callId || this.state !== 'ringing') {
            this._cleanup('answered-elsewhere');
            return;
        }

        await ensurePeer(this);

        if (this.callId !== callId || this.state !== 'ringing') {
            this._cleanup('answered-elsewhere');
            return;
        }

        clearTimeout(this._ringTimer);
        this.state = 'connecting';
        this.emitter.emit('ring:stop');
        this.emitter.emit('connecting');

        this.socket.emit('call:accept', {
            callId,
        });
    }

    reject({ callId }) {
        clearTimeout(this._ringTimer);
        this.emitter.emit('ring:stop');

        this.socket.emit('call:reject', {
            callId,
            reason: 'rejected',
        });

        this._cleanup('rejected');
    }

    hangup(reason = 'ended', _from = null, quality = 0) {
        clearTimeout(this._ringTimer);
        this.emitter.emit('ring:stop');

        if (this.callId) {
            const eventName = this.state === 'ringing' && this.role === 'caller'
                ? 'call:cancel'
                : 'call:end';

            this.socket.emit(eventName, {
                callId: this.callId,
                reason,
                quality,
            });
        }

        this._cleanup(reason);
    }

    toggleMute() {
        const tracks = this.localStream?.getAudioTracks?.() || [];
        tracks.forEach((track) => { track.enabled = !track.enabled; });
        const enabled = tracks[0]?.enabled ?? false;

        this.emitter.emit('local-mic', { enabled });

        if (this.callId) {
            this.socket.emit('call:toggle', {
                callId: this.callId,
                type: 'mute',
                enabled,
            });
        }

        return enabled;
    }

    toggleVideo() {
        const tracks = this.localStream?.getVideoTracks?.() || [];
        tracks.forEach((track) => { track.enabled = !track.enabled; });
        const enabled = tracks[0]?.enabled ?? false;

        this.emitter.emit('local-cam', { enabled });

        if (this.callId) {
            this.socket.emit('call:toggle', {
                callId: this.callId,
                type: 'video',
                enabled,
            });
        }

        return enabled;
    }

    async reportQuality(level) {
        if (!this.callId) return;
        this.socket.emit('call:quality', { callId: this.callId, level });
    }

    async listCameras() {
        return listCameras(this);
    }

    async switchCamera(next = 'auto') {
        return switchCameraTrack(this, next);
    }

    async _getMedia(video) {
        await getMedia(this, video);
        attachAudioGuards(this);
    }

    _cleanup(reason) {
        cleanupPeer(this, reason);
        this._connectedAt = null;
    }

    _deriveLeaderFlags() {
        applyLeaderFlags(this, this.role);
    }

    _reactivateTracks() {
        reactivateTracks(this);
    }

    _beginReconnection(force) {
        beginReconnection(this, force);
    }

    _tryIceRestart() {
        return tryIceRestartIfLeader(this, 'manual');
    }

    _maybeIceRestart(tag = '') {
        return maybeIceRestart(this, tag);
    }

    async _acquireWakeLock() {
        try {
            if ('wakeLock' in navigator && !this._wakeLock) {
                this._wakeLock = await navigator.wakeLock.request('screen');
            }
        } catch (_error) { }
    }

    _releaseWakeLock() {
        try {
            this._wakeLock?.release?.();
        } catch (_error) { }
        this._wakeLock = null;
    }
}

export { ensurePeer, makeOffer };
